import { coercePref } from './coerce-pref.util';

/** The user's stored choice. 'system' follows the OS via prefers-contrast; the other two pin a
 *  contrast level regardless of it. */
export type ContrastPreset = 'system' | 'normal' | 'more';

/**
 * What actually gets painted. Deliberately a SEPARATE type from ContrastPreset, and deliberately
 * nullable: the high-contrast token blocks match on the PRESENCE of `data-contrast='more'`, so
 * "normal contrast" is the attribute being ABSENT — there is no 'normal' attribute value, and
 * writing one would look correct and paint nothing forever.
 */
export type ResolvedContrast = 'more' | null;

/** Key in the /v2/userSettings blob. Frontend-owned and opaque to the backend. */
export const CONTRAST_KEY = 'display.contrast';

/** localStorage key holding the RESOLVED contrast, read by the inline script in index.html.
 *  Namespaced because localStorage is shared across the whole origin. Three states, not two:
 *  'more', the PINNED_OFF_MIRROR sentinel ('off'), and absent — see AttributePresetStore. */
export const CONTRAST_STORAGE_KEY = 'g15.contrast';

export const CONTRAST_PRESETS: readonly ContrastPreset[] = ['system', 'normal', 'more'] as const;

/** Coerce an untrusted value (a settings-blob entry or a cross-tab broadcast payload) to a known
 *  preset. Routes through coercePref so the prototype-key hardening is shared with every other
 *  display.* preference rather than re-hand-rolled here. */
export function coerceContrast(v: unknown): ContrastPreset {
  return coercePref(v, CONTRAST_PRESETS, 'system');
}

/**
 * Map a stored preset to the contrast actually painted.
 *
 * A PURE FUNCTION OF ITS TWO ARGUMENTS, deliberately: `prefersMore` is passed in rather than read
 * from `matchMedia` here, so the mapping is unit-testable under both OS settings without a browser
 * and without stubbing a global. ContrastService owns the media query; this owns the rule.
 *
 * The default keeps the app's behaviour for anyone who does not pass it: no preference expressed,
 * normal contrast.
 */
export function resolveContrast(preset: ContrastPreset, prefersMore = false): ResolvedContrast {
  if (preset === 'more') return 'more';
  if (preset === 'normal') return null;
  return prefersMore ? 'more' : null;
}
